import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  DialogContent,
  Dialog,
  DialogActions,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import Axios from "../axios";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

const CategoryManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [opendel, setOpendel] = useState({ status: false, id: null });
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [parentId, setParentId] = useState("");
  const [hasChildren, setHasChildren] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [write, setWrite] = useState(false);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [existingIcon, setExistingIcon] = useState(null);
  const iconInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = findNavItemByPath(navData, currentPath);
    setWrite(!!matchingNavItem?.write);
  }, [location.pathname, navData]);

  const rootCategories = useMemo(
    () => allRows.filter((r) => !r.parent_id && r.is_active),
    [allRows]
  );

  const tableData = useMemo(
    () =>
      allRows.map((r) => ({
        ...r,
        parent_name: r.parent_id?.name || "",
      })),
    [allRows]
  );

  const fetchCategories = useCallback(() => {
    Axios.get(`${BACKEND_URL}category`)
      .then((res) => {
        setAllRows(res?.data?.data?.docs || []);
      })
      .catch((err) => showError(err));
  }, [BACKEND_URL]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
  }, [fetchCategories]);

  const showError = (err) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Something went wrong";
    toast.error(msg);
  };

  const resetForm = () => {
    setName("");
    setNameError("");
    setParentId("");
    setEditId(null);
    setHasChildren(false);
    setIconFile(null);
    setIconPreview(null);
    setExistingIcon(null);
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setExistingIcon(null);
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const openEditDialog = (row) => {
    setEditId(row._id);
    setName(row.name || "");
    setNameError("");
    setParentId(
      row.parent_id
        ? String(row.parent_id._id || row.parent_id)
        : ""
    );
    const childCount = allRows.filter(
      (r) =>
        r.parent_id &&
        String(r.parent_id._id || r.parent_id) === String(row._id)
    ).length;
    setHasChildren(childCount > 0);
    setExistingIcon(row.icon || null);
    setIconFile(null);
    setIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = "";
    setOpenEdit(true);
  };

  const isNameError = (msg) => {
    const lower = msg.toLowerCase();
    return (
      lower.includes("name already exists") ||
      lower.includes("name is required") ||
      lower.includes("duplicate")
    );
  };

  const addCategory = () => {
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    setSubmitting(true);

    const fd = new FormData();
    fd.append("name", name.trim());
    if (parentId) fd.append("parent_id", parentId);
    fd.append("is_active", "true");
    if (iconFile) fd.append("icon", iconFile);

    Axios.post(`${BACKEND_URL}category`, fd)
      .then((res) => {
        fetchCategories();
        setOpen(false);
        resetForm();
        toast.success(res?.data?.message || "Category added");
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Something went wrong";
        if (isNameError(msg)) {
          setNameError(msg);
        } else {
          showError(err);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const updateCategory = () => {
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    setSubmitting(true);

    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("parent_id", parentId || "");
    if (iconFile) fd.append("icon", iconFile);

    Axios.put(`${BACKEND_URL}category/${editId}`, fd)
      .then((res) => {
        fetchCategories();
        setOpenEdit(false);
        resetForm();
        toast.success(res?.data?.message || "Category updated");
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Something went wrong";
        if (isNameError(msg)) {
          setNameError(msg);
        } else {
          showError(err);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const deleteCategory = () => {
    Axios.delete(`${BACKEND_URL}category/${opendel.id}`)
      .then((res) => {
        fetchCategories();
        setOpendel({ status: false, id: null });
        toast.success(res?.data?.message || "Deleted successfully");
      })
      .catch((err) => showError(err));
  };

  const handleActiveToggle = (event, id, currentActive) => {
    const next = event.target.checked;
    if (next === currentActive) {
      return;
    }
    const req = next
      ? Axios.patch(`${BACKEND_URL}category/${id}/activate`)
      : Axios.patch(`${BACKEND_URL}category/${id}/deactivate`);

    req
      .then((res) => {
        fetchCategories();
        toast.success(res?.data?.message || "Updated successfully");
      })
      .catch((err) => showError(err));
  };

  const columns = [
    {
      name: "parent_name",
      label: "Parent name",
      options: {
        display: false,
        filter: false,
        sort: false,
        download: false,
      },
    },
    {
      name: "icon",
      label: "Icon",
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRenderLite: (i) =>
          tableData[i]?.icon ? (
            <img
              src={tableData[i].icon}
              alt="icon"
              style={{ width: 36, height: 36, objectFit: "contain" }}
            />
          ) : (
            "—"
          ),
      },
    },
    {
      name: "name",
      label: "Name",
    },
    {
      name: "type",
      label: "Type",
      options: {
        customBodyRenderLite: (i) =>
          tableData[i]?.parent_id ? "Subcategory" : "Category",
      },
    },
    {
      name: "parent",
      label: "Parent",
      options: {
        customBodyRenderLite: (i) => {
          const p = tableData[i]?.parent_id;
          if (!p) return "—";
          return p.name || "—";
        },
      },
    },
    {
      name: "dealCount",
      label: "Number of Deals",
      options: {
        filter: false,
        customBodyRenderLite: (i) => tableData[i]?.dealCount ?? 0,
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        customBodyRenderLite: (i) =>
          tableData[i]?.is_active ? "Active" : "Inactive",
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(tableData[i]?.is_active)}
              onChange={(e) =>
                handleActiveToggle(e, tableData[i]?._id, tableData[i]?.is_active)
              }
              size="small"
            />
            <Edit
              sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }}
              onClick={() => openEditDialog(tableData[i])}
            />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() =>
                setOpendel({ status: true, id: tableData[i]?._id })
              }
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  const options = {
    responsive: "standard",
    selectableRows: "none",
    filter: false,
    search: true,
    viewColumns: false,
    rowsPerPageOptions: [],
    download: false,
    customSearch: (searchQuery, row) => {
      if (searchQuery == null || String(searchQuery).trim() === "") {
        return true;
      }
      const q = String(searchQuery).toLowerCase();
      if (row && typeof row === "object" && !Array.isArray(row)) {
        const nm = String(row.name ?? "").toLowerCase();
        const parentNm = String(row.parent_name ?? "").toLowerCase();
        const typeLabel = row.parent_id ? "subcategory" : "category";
        const statusLabel = row.is_active ? "active" : "inactive";
        return (
          nm.includes(q) ||
          parentNm.includes(q) ||
          typeLabel.includes(q) ||
          statusLabel.includes(q)
        );
      }
      if (Array.isArray(row)) {
        return row.some(
          (cell) =>
            cell != null && String(cell).toLowerCase().includes(q)
        );
      }
      return false;
    },
  };

  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 10,
        overflowY: "auto",
      },
    },
  };

  const renderIconUpload = () => {
    const preview = iconPreview || existingIcon;
    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Icon
        </Typography>
        <input
          ref={iconInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          style={{ display: "none" }}
          onChange={handleIconChange}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {preview && (
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <img
                src={preview}
                alt="icon preview"
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  border: "1px solid #e0e0e0",
                  borderRadius: 4,
                }}
              />
              <IconButton
                size="small"
                onClick={clearIcon}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  bgcolor: "white",
                  border: "1px solid #e0e0e0",
                  padding: "2px",
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => iconInputRef.current?.click()}
          >
            {preview ? "Change icon" : "Upload icon"}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          JPG or PNG, max 20MB
        </Typography>
      </Box>
    );
  };

  const renderParentSelect = (disabled) => (
    <FormControl fullWidth className="my-2" disabled={disabled}>
      <InputLabel id="parent-category-label">Category</InputLabel>
      <Select
        labelId="parent-category-label"
        label="Category"
        value={parentId}
        onChange={(e) => setParentId(e.target.value)}
        MenuProps={selectMenuProps}
      >
        <MenuItem value="">
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontStyle: "normal" }}
          >
            Please select category
          </Typography>
        </MenuItem>
        {rootCategories
          .filter((c) => String(c._id) !== String(editId))
          .map((c) => (
            <MenuItem key={c._id} value={String(c._id)}>
              {c.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Category</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Categories</h2>
        {write && (
          <Button
            variant="contained"
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add category
          </Button>
        )}
      </div>

      <div className="table-responsive">
        <MUIDataTable
          title="Category list"
          data={tableData}
          columns={columns}
          options={options}
        />
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Add category</h3>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <TextField
            label="Name"
            variant="outlined"
            required
            fullWidth
            className="my-2"
            onChange={(e) => {
              setName(e.target.value);
              setNameError("");
            }}
            value={name}
            error={Boolean(nameError)}
            helperText={nameError}
          />
          {renderParentSelect(false)}
          {renderIconUpload()}
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            onClick={addCategory}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Edit category</h3>
          <IconButton
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
          >
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <TextField
            label="Name"
            variant="outlined"
            required
            fullWidth
            className="my-2"
            onChange={(e) => {
              setName(e.target.value);
              setNameError("");
            }}
            value={name}
            error={Boolean(nameError)}
            helperText={nameError}
          />
          {hasChildren && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This category has subcategories; parent cannot be changed until
              subcategories are removed or reassigned.
            </Typography>
          )}
          {renderParentSelect(hasChildren)}
          {renderIconUpload()}
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel-button"
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            onClick={updateCategory}
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={opendel.status}
        onClose={() => setOpendel({ status: false, id: null })}
        fullWidth
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete category</h3>
          <IconButton onClick={() => setOpendel({ status: false, id: null })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          Are you sure you want to delete this category?
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel-button"
            onClick={() => setOpendel({ status: false, id: null })}
          >
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            onClick={deleteCategory}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CategoryManagement;
